/**
 * EPIC284 discriminated measurement contract — frontend derivation helpers.
 * Authoritative measurement type is carried on API responses; these helpers
 * derive presentation properties without persisting contradictory fields.
 */

export type MeasurementValueType =
  | 'OPERATIONAL_ACTIVITY'
  | 'OPERATIONAL_RATE'
  | 'REVIEWED_EXHAUSTIVE_RATE'
  | 'REVIEWED_ESTIMATED_RATE';

export type ValidationMode = 'NONE' | 'EXHAUSTIVE' | 'SAMPLED';

export type ClaimClass = 'OPERATIONAL' | 'CLAIM_BEARING';

export type OperationalCurrentMeasurement =
  | {
    type: 'OPERATIONAL_ACTIVITY';
    validationMode: 'NONE';
    claimClass: 'OPERATIONAL';
    activityCount: number;
    ratePerHour: null;
  }
  | {
    type: 'OPERATIONAL_RATE';
    validationMode: 'NONE';
    claimClass: 'OPERATIONAL';
    activityCount: number;
    usableHours: number;
    ratePerHour: number;
  };

export type ReviewedExhaustiveRate = {
  type: 'REVIEWED_EXHAUSTIVE_RATE';
  validationMode: 'EXHAUSTIVE';
  claimClass: 'CLAIM_BEARING';
  confirmedCount: number;
  usableHours: number;
  ratePerHour: number;
};

export type ReviewedEstimatedRate = {
  type: 'REVIEWED_ESTIMATED_RATE';
  validationMode: 'SAMPLED';
  claimClass: 'CLAIM_BEARING';
};

export type MeasurementValue =
  | OperationalCurrentMeasurement
  | ReviewedExhaustiveRate
  | ReviewedEstimatedRate;

/** Amendment A — hold Current operational rate until Tasks 2+3 acceptance. */
export const OPERATIONAL_RATE_DISPLAY_ENABLED = false;

/** Amendment B — empty frozen set attestation reason (exact match). */
export const EMPTY_POPULATION_ATTESTATION_REASON = 'no items eligible';

export type AdmitEligibleRecallFreeze = {
  conditionId: string;
  cameraId: string;
  modelVersion: string;
  admitEligible: true;
};

export class InvalidCurrentMeasurementError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidCurrentMeasurementError';
  }
}

export class InvalidMeasurementValueError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidMeasurementValueError';
  }
}

/**
 * Current may only render operational measurement types as its headline.
 * Throw — never fall back to a default claim-bearing render.
 */
export function assertOperationalMeasurement(
  measurement: MeasurementValue,
): asserts measurement is OperationalCurrentMeasurement {
  if (
    measurement.type !== 'OPERATIONAL_ACTIVITY'
    && measurement.type !== 'OPERATIONAL_RATE'
  ) {
    throw new InvalidCurrentMeasurementError(
      'Current cannot render a claim-bearing measurement as its headline value.',
    );
  }
}

export function getValidationMode(
  type: 'OPERATIONAL_ACTIVITY' | 'OPERATIONAL_RATE',
): 'NONE';
export function getValidationMode(type: 'REVIEWED_EXHAUSTIVE_RATE'): 'EXHAUSTIVE';
export function getValidationMode(type: 'REVIEWED_ESTIMATED_RATE'): 'SAMPLED';
export function getValidationMode(type: MeasurementValueType): ValidationMode;
export function getValidationMode(type: MeasurementValueType): ValidationMode {
  switch (type) {
    case 'OPERATIONAL_ACTIVITY':
    case 'OPERATIONAL_RATE':
      return 'NONE';
    case 'REVIEWED_EXHAUSTIVE_RATE':
      return 'EXHAUSTIVE';
    case 'REVIEWED_ESTIMATED_RATE':
      return 'SAMPLED';
  }
}

export function getClaimClass(
  type: 'OPERATIONAL_ACTIVITY' | 'OPERATIONAL_RATE',
): 'OPERATIONAL';
export function getClaimClass(
  type: 'REVIEWED_EXHAUSTIVE_RATE' | 'REVIEWED_ESTIMATED_RATE',
): 'CLAIM_BEARING';
export function getClaimClass(type: MeasurementValueType): ClaimClass;
export function getClaimClass(type: MeasurementValueType): ClaimClass {
  switch (type) {
    case 'OPERATIONAL_ACTIVITY':
    case 'OPERATIONAL_RATE':
      return 'OPERATIONAL';
    case 'REVIEWED_EXHAUSTIVE_RATE':
    case 'REVIEWED_ESTIMATED_RATE':
      return 'CLAIM_BEARING';
  }
}

/**
 * Reject independently-persisted contradictions.
 * OPERATIONAL_RATE + EXHAUSTIVE + CLAIM_BEARING is unconstructible.
 */
export function assertDerivedMeasurementProps(measurement: MeasurementValue): void {
  const expectedMode = getValidationMode(measurement.type);
  const expectedClass = getClaimClass(measurement.type);
  if (measurement.validationMode !== expectedMode) {
    throw new InvalidMeasurementValueError(
      `validationMode must be derived from type (${measurement.type} → ${expectedMode})`,
    );
  }
  if (measurement.claimClass !== expectedClass) {
    throw new InvalidMeasurementValueError(
      `claimClass must be derived from type (${measurement.type} → ${expectedClass})`,
    );
  }
  if (
    measurement.type === 'OPERATIONAL_RATE'
    && (measurement.validationMode !== 'NONE' || measurement.claimClass !== 'OPERATIONAL')
  ) {
    throw new InvalidMeasurementValueError(
      'OPERATIONAL_RATE cannot carry EXHAUSTIVE or CLAIM_BEARING properties',
    );
  }
}

/** Constructor — callers must not supply an arbitrary rate. */
export function computeOperationalRatePerHour(
  activityCount: number | null | undefined,
  usableHours: number | null | undefined,
): number | null {
  if (
    activityCount == null
    || !Number.isFinite(activityCount)
    || activityCount < 0
    || usableHours == null
    || !Number.isFinite(usableHours)
    || usableHours <= 0
  ) {
    return null;
  }
  return Math.round((activityCount / usableHours) * 1000) / 1000;
}

/**
 * Resolve Current operational measurement from activity + usable time.
 * Review gates are intentionally ignored — Current is operational.
 */
export function resolveOperationalCurrentMeasurement(params: {
  activityCount: number | null | undefined;
  usableHours: number | null | undefined;
}): OperationalCurrentMeasurement | null {
  const activity = params.activityCount;
  if (activity == null || !Number.isFinite(activity) || activity < 0) {
    return null;
  }
  const rate = computeOperationalRatePerHour(activity, params.usableHours);
  if (rate == null || params.usableHours == null) {
    const activityMeasurement: OperationalCurrentMeasurement = {
      type: 'OPERATIONAL_ACTIVITY',
      validationMode: getValidationMode('OPERATIONAL_ACTIVITY'),
      claimClass: getClaimClass('OPERATIONAL_ACTIVITY'),
      activityCount: activity,
      ratePerHour: null,
    };
    assertDerivedMeasurementProps(activityMeasurement);
    return activityMeasurement;
  }
  const rateMeasurement: OperationalCurrentMeasurement = {
    type: 'OPERATIONAL_RATE',
    validationMode: getValidationMode('OPERATIONAL_RATE'),
    claimClass: getClaimClass('OPERATIONAL_RATE'),
    activityCount: activity,
    usableHours: params.usableHours,
    ratePerHour: rate,
  };
  assertDerivedMeasurementProps(rateMeasurement);
  return rateMeasurement;
}

/** Reserved — estimated rates are not implemented. */
export function createReviewedEstimatedRate(_args?: unknown): never {
  throw new Error('REVIEWED_ESTIMATED_RATE is not implemented');
}

/**
 * Amendment B — REVIEWED_EXHAUSTIVE_RATE with confirmedCount=0 requires an
 * admit-eligible recall freeze for exact (conditionId, cameraId, modelVersion).
 */
export function createReviewedExhaustiveRate(params: {
  confirmedCount: number;
  usableHours: number;
  admitEligibleRecallFreeze?: AdmitEligibleRecallFreeze | null;
  conditionId?: string | null;
  cameraId?: string | null;
  modelVersion?: string | null;
}): ReviewedExhaustiveRate {
  const confirmed = params.confirmedCount;
  const usable = params.usableHours;
  if (!Number.isFinite(confirmed) || confirmed < 0 || !Number.isInteger(confirmed)) {
    throw new InvalidMeasurementValueError('confirmedCount invalid');
  }
  if (!Number.isFinite(usable) || usable <= 0) {
    throw new InvalidMeasurementValueError('usableHours invalid');
  }
  if (confirmed === 0) {
    const freeze = params.admitEligibleRecallFreeze;
    if (!freeze || freeze.admitEligible !== true) {
      throw new InvalidMeasurementValueError(
        'zero-numerator REVIEWED_EXHAUSTIVE_RATE requires admit-eligible recall freeze',
      );
    }
    const conditionId = String(freeze.conditionId || '').trim();
    const cameraId = String(freeze.cameraId || '').trim();
    const modelVersion = String(freeze.modelVersion || '').trim();
    if (!conditionId || !cameraId || !modelVersion) {
      throw new InvalidMeasurementValueError(
        'admit-eligible recall freeze identity incomplete',
      );
    }
    const expectedCondition = String(params.conditionId || '').trim();
    const expectedCamera = String(params.cameraId || '').trim();
    const expectedModel = String(params.modelVersion || '').trim();
    if (expectedCondition && conditionId !== expectedCondition) {
      throw new InvalidMeasurementValueError(
        'admit-eligible recall freeze condition mismatch',
      );
    }
    if (expectedCamera && cameraId !== expectedCamera) {
      throw new InvalidMeasurementValueError(
        'admit-eligible recall freeze camera mismatch',
      );
    }
    if (expectedModel && modelVersion !== expectedModel) {
      throw new InvalidMeasurementValueError(
        'admit-eligible recall freeze model mismatch',
      );
    }
  }
  const measurement: ReviewedExhaustiveRate = {
    type: 'REVIEWED_EXHAUSTIVE_RATE',
    validationMode: getValidationMode('REVIEWED_EXHAUSTIVE_RATE'),
    claimClass: getClaimClass('REVIEWED_EXHAUSTIVE_RATE'),
    confirmedCount: confirmed,
    usableHours: usable,
    ratePerHour: Math.round((confirmed / usable) * 1000) / 1000,
  };
  assertDerivedMeasurementProps(measurement);
  return measurement;
}

/** Short label fragment for supporting activity until episode definition vNext. */
export const PROVISIONAL_GROUPING_NOTE =
  'provisional grouping until episode definition vNext';

/**
 * EPIC284 Task 6.1 — customer-facing caveat wherever a provisional count/rate appears
 * (Current and Recurring). Trustworthy history starts at episode definition vNext.
 */
export const PROVISIONAL_GROUPING_CAVEAT =
  'Episode activity is provisional until episode definition vNext is deployed.';

/**
 * Amendment C2 — post-drop / rebuilt series is not the start of trustworthy history.
 * The trustworthy Recurring clock starts at episode definition vNext.
 */
export const RECURRING_TRUSTWORTHY_HISTORY_NOTE =
  'This series is not the start of trustworthy history. Trustworthy Recurring history starts when episode definition vNext is deployed.';

/**
 * Current Overview header/support copy.
 * Clarifies: activity may show without review; a rate still needs applicable gates.
 * Does NOT mean Current never needs review for a rate (Amendment A still holds the rate).
 */
export const OPERATIONAL_CURRENT_SUPPORT =
  'Each condition shows operational activity from the latest measured window. Human review is not required to show activity; a rate would still require the applicable gates (coverage; and for claim-bearing rates, review).';

/** Informational copy when no formal review population exists (not an error). */
export const REVIEW_LIST_NOT_OPENED_INFO =
  'Episode activity recorded; formal review list not opened for this window.';

/** Compact progress/blocker label when no enumerable review set exists. */
export const REVIEW_LIST_NOT_OPENED =
  'Formal review list not opened for this window';

/** Current / Recurring activity count — never unreviewed / qualifying / claim-bearing. */
export function formatProvisionalEpisodesLabel(count: number | string): string {
  return `${count} provisional episodes recorded`;
}

/** Recurring summary line (Task 6.2) — episode total across measured periods. */
export function formatRecurringProvisionalEpisodesLabel(count: number | string): string {
  return `${formatProvisionalEpisodesLabel(count)} across available periods`;
}

/**
 * Since Control claim packaging gate (EP284-P5).
 * Basis fingerprint / comparisonStatus alone is not enough — claim surfaces
 * require reviewed-exhaustive rate issuance facts. Operational activity must
 * never authorize a valid comparison claim.
 */
export type SinceControlClaimGateInput = {
  comparisonStatus?: string | null;
  coverageGatePassed?: boolean | null;
  reviewGatePassed?: boolean | null;
  reviewedObservationCount?: number | null;
  ratePerHour?: number | null;
};

export function sinceControlClaimComparisonAllowed(
  input: SinceControlClaimGateInput,
): boolean {
  const status = String(input.comparisonStatus || '').trim().toLowerCase();
  if (status !== 'comparable') return false;
  if (input.coverageGatePassed !== true) return false;
  if (input.reviewGatePassed !== true) return false;
  const reviewed = input.reviewedObservationCount;
  if (reviewed == null || !Number.isFinite(reviewed) || reviewed < 0) {
    return false;
  }
  const rate = input.ratePerHour;
  if (rate == null || !Number.isFinite(rate)) return false;
  return true;
}
